import { HttpParams } from "@angular/common/http";
import { forwardRef, Inject, Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { firstValueFrom } from "rxjs";
import { DATA_IMPORT_BY_ID, DATA_IMPORTS, DATA_IMPORTS_V2 } from "../../common/endpoints";
import { DataImport, PagedList } from "../../model";
import { CustomLabelService } from "../../service/custom-label.service";
import { HttpService } from "../../service/http.service";
import { AbstractThingContextService } from "../../shared/class/abstract-thing-context-service.class";
import { LocalizationPipe } from "../../shared/pipe";
import { HttpUtility } from "../../utility";

@Injectable()
export class DataImportWidgetService {

    constructor(
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => CustomLabelService)) private labelService: CustomLabelService,
        @Inject(forwardRef(() => MatSnackBar)) private snackBar: MatSnackBar,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => HttpUtility)) private httpUtility: HttpUtility
    ) { }

    getPagedDataImports(sort: string[], pageIndex: number, pageSize: number, searchText: string): Promise<PagedList<DataImport>> {
        const params = this.getParams(sort, pageIndex, pageSize, searchText);
        return firstValueFrom(this.httpService.get<PagedList<DataImport>>(DATA_IMPORTS_V2, params));
    }

    private getParams(sort: string[], pageIndex: number, pageSize: number, searchText: string): HttpParams {
        let params = new HttpParams();
        params = params.set('page', pageIndex + '');
        params = params.set('size', pageSize + '');
        if (sort && sort[0]) {
            params = params.set('sort', sort.join(','));
        }
        params = params.set('thingId', this.thingContextService.getCurrentThing().id);
        if (searchText) {
            params = params.set('searchText', searchText);
        }
        return params;
    }

    showSnackbar(text: string, isError?: boolean): void {
        this.labelService.getCustomLabel(text)
            .then(message => {
                this.snackBar.open(this.localizationPipe.transform(message), '', {
                    duration: 4000,
                    panelClass: isError ? 'notification-error' : 'notification-info'
                });
            });
    }

    performDataImport(formData: FormData, reconcileConnectionStatus: boolean): Promise<DataImport> {
        const currentThing = this.thingContextService.getCurrentThing();
        let params = new HttpParams().set('thingId', currentThing.id);
        if (reconcileConnectionStatus) {
            params = params.set("reconcileConnectionStatus", reconcileConnectionStatus);
        }
        return firstValueFrom(this.httpService.post<DataImport>(DATA_IMPORTS, formData, params));
    }

    getDataImportById(id: string): Promise<DataImport> {
        return firstValueFrom(this.httpService.get<DataImport>(DATA_IMPORT_BY_ID.replace('{id}', id)));
    }

    downloadFromExternalUrl(dataImportName: string, externalUrl: string, dateTime: number): void {
        const dateInput = dateTime.toString();
        const name = (dataImportName ? (dataImportName + '-') : '') + dateInput.substring(0, 8) + "-" + dateInput.substring(8);
        firstValueFrom(this.httpService.externalGet(externalUrl, { responseType: 'blob' })).then(blob => {
            this.httpUtility.wrapFileAndDownload({ file: blob, fileName: name + '.csv' });
        });
    }

}